import logoBlack from "@assets/commentt_ai_logo_black.svg";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { Sun, Moon, LogOut, User as UserIcon, Building, ChevronDown } from "lucide-react";
import { Link } from "wouter";
import { useTheme } from "@/components/ui/theme-provider";
import type { Organization, User } from "@shared/schema";

interface DashboardHeaderProps {
  organization: Organization;
  currentUser: User;
  onThemeToggle?: () => void;
  theme?: "light" | "dark";
}

export function DashboardHeader({ 
  organization, 
  currentUser, 
}: DashboardHeaderProps) {
  const { theme, toggleTheme } = useTheme();

  const handleLogout = async () => {
    try {
      await fetch('/api/auth/logout', { method: 'POST' });
    } catch (_) {}
    window.location.href = '/login';
  };

  const initials = (currentUser.firstName || currentUser.email || 'U').charAt(0).toUpperCase();

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur-md">
      <div className="w-full max-w-6xl mx-auto px-4 sm:px-6">
        <div className="flex justify-between items-center h-14">
          <Link href="/">
            <img
              src={logoBlack}
              alt="Commentt.ai"
              className="h-3 cursor-pointer dark:invert"
            />
          </Link>

          <div className="flex items-center gap-1.5">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="h-8 w-8 text-muted-foreground hover:text-foreground"
            >
              {theme === "dark" ? (
                <Sun className="h-4 w-4" />
              ) : (
                <Moon className="h-4 w-4" />
              )}
            </Button>

            <Popover>
              <PopoverTrigger asChild>
                <button className="flex items-center gap-2 h-8 px-1.5 rounded-md hover:bg-accent transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-ring">
                  <Avatar className="h-6 w-6">
                    <AvatarFallback className="bg-primary/15 text-primary text-xs font-medium">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden sm:block text-sm text-foreground max-w-[120px] truncate">
                    {currentUser.firstName || currentUser.email || 'User'}
                  </span>
                  <ChevronDown className="h-3 w-3 text-muted-foreground hidden sm:block" />
                </button>
              </PopoverTrigger>
              <PopoverContent 
                className="w-48 p-1" 
                align="end"
                sideOffset={6}
              >
                {(currentUser.role === 'admin' || currentUser.role === 'manager') && (
                  <Link href="/account-settings">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full justify-start gap-2 h-8 text-sm font-normal"
                    >
                      <UserIcon className="h-3.5 w-3.5" />
                      Account Settings
                    </Button>
                  </Link>
                )}
                
                {currentUser.role === 'admin' && (
                  <Link href="/org-settings">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full justify-start gap-2 h-8 text-sm font-normal"
                    >
                      <Building className="h-3.5 w-3.5" />
                      Org Settings
                    </Button>
                  </Link>
                )}
                
                {(currentUser.role === 'admin' || currentUser.role === 'manager') && (
                  <Separator className="my-1" />
                )}
                
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleLogout}
                  className="w-full justify-start gap-2 h-8 text-sm font-normal text-destructive hover:text-destructive hover:bg-destructive/10"
                >
                  <LogOut className="h-3.5 w-3.5" />
                  Log out
                </Button>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </div>
    </header>
  );
}
